import { CashFlowStatus } from 'api/models/cash-flow-status';
import openNotificationWithIcon from './notification';
import dayjs from 'dayjs';
type QueryValue = string | string[];

/**
 * Return ellipsis of a given string
 * @param text
 * @param size
 */
const ellipsis = (text: string, size: number): string => {
  return `${text
    .split(' ')
    .slice(0, size)
    .join(' ')}...`;
};

const truncate = (text: string, size: number): string => (text.length > size ? `${text.substring(0, size)}...` : text);

interface ServerError {
  errors?: Record<string, string[]>;
  detail?: string;
}

interface ErrorType {
  response?: {
    status: number;
    data: ServerError;
  };
  title?: string;
}

const showServerErrors = (error: ErrorType): void => {
  const _error = { ...error };

  if (_error.response && [400, 500].includes(_error.response.status)) {
    const serverError = _error.response.data;
    let errorMessages = '';
    if (serverError.errors) {
      Object.keys(serverError.errors).forEach(key => {
        errorMessages += serverError.errors![key].join('\n');
      });
    } else if (serverError.detail) {
      errorMessages += serverError.detail;
    }

    openNotificationWithIcon('error', error.title || '', errorMessages);
  } else if (_error.response && _error.response.status === 401) {
    window.location.replace('/authentication/login');
  } else if (_error.response && _error.response.status === 403) {
    window.location.replace('/error/403');
  } else if (_error.response && _error.response.status === 404) {
    window.location.replace('/error/404');
  }
};

const actionCreator = <T>(type: string, payload: T = {} as T) => {
  return { type, payload };
};

const getAdjustedFilters = (filters: Record<string, { value?: any } | any>) => {
  const adjustedFilters: Record<string, any> = {};
  if (filters) {
    Object.keys(filters).forEach(x => {
      const item = filters[x];
      if (item) {
        adjustedFilters[x] = item.value || item;
      }
    });
  }
  return adjustedFilters;
};

/**
 *
 * @param filters
 * @returns object
 */
const formatObjectFiltersFromSelect = (filters: Record<string, { value?: string } | boolean>) => {
  let objectFilters: Record<string, string | boolean> = {};

  Object.entries(filters).forEach(([key, e]) => {
    if (typeof e === 'boolean') {
      objectFilters[key] = e;
    } else {
      objectFilters[key] = e?.value || '';
    }
  });

  return objectFilters;
};

interface LocationProps {
  pathname: string;
  search?: string;
  hash?: string;
}

function getLocationId({ pathname, search, hash }: LocationProps): string {
  return pathname + (search ? '?' + search : '') + (hash ? '#' + hash : '');
}

const getFilteredSearchParamsByKey = (
  key: string,
  params: URLSearchParams = new URLSearchParams(window.location.search),
) => {
  params.delete(key);
  return params.toString();
};

const base64Encode = (str: string): string => btoa(str);

const parseQueryAsNumber = (value: QueryValue, defaultValue?: number): number | undefined => {
  if (Array.isArray(value)) {
    value = value[0];
  }

  if (!value) return defaultValue;

  const intValue = parseFloat(value.replace(',', '.'));
  return !isNaN(intValue) ? intValue : defaultValue;
};

const parseQueryAsEnum = <T>(value: QueryValue, defaultValue?: T): T | null | undefined => {
  if (value === '' || value === null) {
    return null;
  }

  const valueActual = parseQueryAsNumber(value) || value;
  return valueActual != null ? ((valueActual as unknown) as T) : defaultValue;
};

const humanFileSize = (size: number | null | undefined): string => {
  if (size == null) return '0 B';
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return ((size / Math.pow(1024, i)) as any).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
};

const shortenFileName = (fileName: string | null | undefined, length: number = 10): string => {
  if (!fileName) return '';
  // Check if the input filename is already shorter than or equal to the target length
  if (fileName.length <= length) return fileName;

  // Extract file extension
  const ext = fileName.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2);

  // Calculate the amount of characters to keep from the start and end of the filename
  const charsFromStart = (length - ext.length - 3) / 2;
  const charsFromEnd = length - ext.length - 3 - charsFromStart;

  // Return shortened file name
  return `${fileName.substr(0, charsFromStart)}...${fileName.substr(-charsFromEnd)}.${ext}`;
};

// Function to convert a string into a lowercase, dashed format
const toDashedKey = (str: any) =>
  (str ?? '')
    .toLowerCase() // Convert to lowercase
    .normalize('NFD') // Normalize string to decompose combined graphemes
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s]/g, '') // Remove non-alphanumeric characters except spaces
    .trim() // Trim leading and trailing spaces
    .replace(/\s+/g, '-'); // Replace spaces with dashes

// src/utility/formatters.ts

/**
 * Formats a number as currency with the specified locale and currency code
 * @param value - Number to format
 * @param locale - Locale to use for formatting (default: 'en-US')
 * @param currency - Currency code to use (default: 'EUR')
 * @returns Formatted currency string
 */
export const formatCurrency = (value?: number | null, locale = 'en-US', currency = 'EUR'): string => {
  if (value == null) return '-';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Formats a date string or Date object into a localized string
 * @param date - Date to format (string or Date object)
 * @param locale - Locale to use for formatting (default: 'en-US')
 * @param options - Intl.DateTimeFormatOptions to customize the output
 * @returns Formatted date string
 */
export const formatDateTime = (
  date?: string | Date | null,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
): string => {
  if (!date) return '-';

  const dateObject = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObject.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat(locale, options).format(dateObject);
};

/**
 * Formats a date string or Date object into a localized string
 * @param date - Date to format (string or Date object)
 * @returns Formatted date string
 */
export const formatDate = (
  date?: string | Date | null
): string => {
  if (!date) return '-';

  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Formats a date as a relative time string (e.g., "2 days ago")
 * @param date - Date to format
 * @param locale - Locale to use for formatting (default: 'en-US')
 * @returns Relative time string
 */
export const formatRelativeTime = (date?: string | Date | null, locale = 'en-US'): string => {
  if (!date) return '-';

  const dateObject = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObject.getTime())) {
    return '-';
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObject.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second');
  } else if (diffInSeconds < 3600) {
    return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
  } else if (diffInSeconds < 86400) {
    return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
  } else if (diffInSeconds < 2592000) {
    return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
  } else if (diffInSeconds < 31536000) {
    return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
  } else {
    return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
  }
};

/**
 * Formats a number with thousand separators
 * @param value - Number to format
 * @param locale - Locale to use for formatting (default: 'en-US')
 * @returns Formatted number string
 */
export const formatNumber = (value?: number | null, locale = 'en-US'): string => {
  if (value == null) return '-';

  return new Intl.NumberFormat(locale).format(value);
};

export const formatSquareMeters = (value: number) => {
  if (!value) return '0 m²';
  return `${value.toFixed(2)} m²`;
};

export const formatPercentage = (value: number, total: number) => `${((value / total) * 100).toFixed(1)}%`;

export const getCashFlowStatusColor = (status: CashFlowStatus) => {
  switch (status) {
    case CashFlowStatus.PartiallyPaidOnTime:
      return 'blue';
    case CashFlowStatus.PartiallyPaidNotOnTime:
      return 'purple';
    case CashFlowStatus.PaidOnTime:
      return 'green';
    case CashFlowStatus.PaidNotOnTime:
      return 'orange';
    case CashFlowStatus.OverpaidOnTime:
      return 'teal';
    case CashFlowStatus.OverpaidNotOnTime:
      return 'darkorange';
    case CashFlowStatus.Overdue:
      return 'red';
    case CashFlowStatus.Pending:
      return 'gold';
    case CashFlowStatus.Cancelled:
      return 'gray';
    default:
      return 'default';
  }
};

export const getCashFlowStatusText = (status: CashFlowStatus): { key: string; title: string } => {
  switch (status) {
    case CashFlowStatus.PartiallyPaidOnTime:
      return { key: 'partiallyPaidOnTime', title: 'Partially Paid (On Time)' };
    case CashFlowStatus.PartiallyPaidNotOnTime:
      return { key: 'partiallyPaidNotOnTime', title: 'Partially Paid (Not On Time)' };
    case CashFlowStatus.PaidOnTime:
      return { key: 'paidOnTime', title: 'Paid (On Time)' };
    case CashFlowStatus.PaidNotOnTime:
      return { key: 'paidNotOnTime', title: 'Paid (Not On Time)' };
    case CashFlowStatus.OverpaidOnTime:
      return { key: 'overpaidOnTime', title: 'Overpaid (On Time)' };
    case CashFlowStatus.OverpaidNotOnTime:
      return { key: 'overpaidNotOnTime', title: 'Overpaid (Not On Time)' };
    case CashFlowStatus.Overdue:
      return { key: 'overdue', title: 'Overdue' };
    case CashFlowStatus.Pending:
      return { key: 'pending', title: 'Pending' };
    case CashFlowStatus.Cancelled:
      return { key: 'cancelled', title: 'Cancelled' };
    default:
      return { key: 'unknown', title: 'Unknown' };
  }
};

const formatImagePath = (filePath: string | null | undefined): string => {
  if (!filePath) return '';
  return filePath;
};

const getFileIcon = (fileName: string) => {
  const extension = fileName
    .split('.')
    .pop()
    ?.toLowerCase();

  switch (extension) {
    // Image files
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
    case 'svg':
      return { icon: 'image', color: '#13c2c2' }; // Cyan color for images
    // Existing document types
    case 'pdf':
      return { icon: 'file-text', color: '#ff4d4f' };
    case 'doc':
    case 'docx':
      return { icon: 'file-text', color: '#1890ff' };
    case 'xls':
    case 'xlsx':
      return { icon: 'file-text', color: '#52c41a' };
    case 'txt':
      return { icon: 'file-text', color: '#666666' };
    case 'md':
      return { icon: 'file-text', color: '#722ed1' };
    case 'csv':
      return { icon: 'file-text', color: '#faad14' };
    default:
      return { icon: 'file', color: '#8c8c8c' };
  }
};

export const updatePageTitle = (organizationName?: string) => {
  document.title = organizationName ? `CRM | ${organizationName}` : 'CRM';
};

export const updateFavicon = (organizationLogo?: string) => {
  const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (link) {
    link.href = organizationLogo || '/favicon.ico';
  }
};

const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => ({
        ...acc,
        [key.charAt(0).toLowerCase() + key.slice(1)]: toCamelCase(obj[key]),
      }),
      {},
    );
  }
  return obj;
};

export {
  ellipsis,
  getFilteredSearchParamsByKey,
  truncate,
  actionCreator,
  showServerErrors,
  getAdjustedFilters,
  getLocationId,
  formatObjectFiltersFromSelect,
  base64Encode,
  parseQueryAsEnum,
  parseQueryAsNumber,
  shortenFileName,
  humanFileSize,
  toDashedKey,
  formatImagePath,
  getFileIcon,
  toCamelCase,
};
